/**
 * VacationReminderBanner - Zeigt unbestätigte Urlaubshinweise an
 * 
 * Wird auf der Startseite (Dashboard) und Urlaubsseite (LeavePage) angezeigt.
 * Mitarbeiter können Hinweise mit "Zur Kenntnis genommen" bestätigen.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  AlertCircle,
  Info,
  Calendar,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Bell,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

export function VacationReminderBanner({ showFull = false }) {
  const { api, user } = useAuth();
  const [reminders, setReminders] = useState({ unacknowledged: [], reminders: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [expandedReminder, setExpandedReminder] = useState(null);
  const [acknowledging, setAcknowledging] = useState(null);

  const fetchReminders = useCallback(async () => {
    try {
      const res = await api.get('/vacation-accounts/reminders/me');
      setReminders(res.data);
    } catch (error) {
      console.error('Failed to fetch vacation reminders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleAcknowledge = async (reminderId) => {
    setAcknowledging(reminderId);
    try {
      await api.post(`/vacation-accounts/reminders/${reminderId}/acknowledge`);
      toast.success('Hinweis zur Kenntnis genommen');
      fetchReminders();
    } catch (error) {
      toast.error('Fehler beim Bestätigen');
    } finally {
      setAcknowledging(null);
    }
  };

  const getReminderTypeLabel = (type) => {
    switch (type) {
      case 'annual_notice':
        return 'Jährlicher Urlaubshinweis';
      case 'expiry_warning':
        return 'Verfallswarnung';
      case 'carryover_warning':
        return 'Übertrag-Verfallswarnung';
      default:
        return 'Urlaubshinweis';
    }
  };

  const getReminderTypeColor = (type) => {
    switch (type) {
      case 'annual_notice':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
      case 'expiry_warning':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-400';
      case 'carryover_warning':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
      default:
        return 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400';
    }
  };

  const getReminderIcon = (type) => {
    switch (type) {
      case 'annual_notice':
        return <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'expiry_warning':
        return <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
      case 'carryover_warning':
        return <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />;
      default:
        return <Bell className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'd. MMMM yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return null;
  }

  const unacknowledged = reminders.unacknowledged || [];

  // Wenn keine unbestätigten Hinweise und nicht showFull, nichts anzeigen
  if (unacknowledged.length === 0 && !showFull) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="vacation-reminder-banner">
      {/* Unbestätigte Hinweise als Banner */}
      <AnimatePresence>
        {unacknowledged.map((reminder) => (
          <motion.div
            key={reminder.id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`rounded-lg border p-4 ${getReminderTypeColor(reminder.reminder_type)}`}
            data-testid={`reminder-banner-${reminder.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                {getReminderIcon(reminder.reminder_type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">
                      {getReminderTypeLabel(reminder.reminder_type)}
                    </p>
                    <p className="text-sm opacity-80 mt-1">
                      {reminder.days_remaining_at_send?.toFixed(1)} Tage offen 
                      {reminder.expiry_date_referenced && (
                        <> - verfällt am {formatDate(reminder.expiry_date_referenced)}</>
                      )}
                    </p>
                  </div>
                  
                  <Button
                    size="sm"
                    onClick={() => handleAcknowledge(reminder.id)}
                    disabled={acknowledging === reminder.id}
                    className="bg-white/20 hover:bg-white/30 text-current border-0"
                    data-testid={`acknowledge-reminder-${reminder.id}`}
                  >
                    {acknowledging === reminder.id ? (
                      <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Zur Kenntnis genommen
                      </>
                    )}
                  </Button>
                </div>

                {/* Erweiterbarer Inhalt */}
                <button
                  onClick={() => setExpandedReminder(
                    expandedReminder === reminder.id ? null : reminder.id
                  )}
                  className="text-sm underline opacity-70 hover:opacity-100 mt-2 flex items-center gap-1"
                >
                  {expandedReminder === reminder.id ? (
                    <>Weniger anzeigen <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Details anzeigen <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>

                <AnimatePresence>
                  {expandedReminder === reminder.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 p-3 bg-black/20 rounded-lg text-sm whitespace-pre-wrap font-mono leading-relaxed">
                        {reminder.content_snapshot}
                      </div>
                      <p className="text-xs opacity-60 mt-2">
                        Versendet am {formatDate(reminder.sent_at)}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Vollständige Liste (für Urlaubsseite) */}
      {showFull && (
        <div className="card-glass p-6" data-testid="vacation-reminders-list">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            Meine Urlaubshinweise
          </h3>

          {reminders.reminders?.length > 0 ? (
            <div className="space-y-3">
              {reminders.reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className={`p-4 rounded-lg border ${
                    reminder.acknowledged_at 
                      ? 'bg-muted/50 border-border' 
                      : getReminderTypeColor(reminder.reminder_type)
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getReminderIcon(reminder.reminder_type)}
                      <div>
                        <p className={`font-medium ${reminder.acknowledged_at ? 'text-muted-foreground' : ''}`}>
                          {getReminderTypeLabel(reminder.reminder_type)} {reminder.year}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(reminder.sent_at)}
                        </p>
                      </div>
                    </div>
                    
                    {reminder.acknowledged_at ? (
                      <span className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Bestätigt am {formatDate(reminder.acknowledged_at)}
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleAcknowledge(reminder.id)}
                        disabled={acknowledging === reminder.id}
                        className="bg-indigo-500 hover:bg-indigo-600"
                      >
                        Bestätigen
                      </Button>
                    )}
                  </div>
                  
                  {/* Kompakte Details */}
                  <div className="mt-2 text-sm text-muted-foreground">
                    {reminder.days_remaining_at_send?.toFixed(1)} Tage zum Zeitpunkt des Hinweises
                    {reminder.expiry_date_referenced && (
                      <> • Verfall: {formatDate(reminder.expiry_date_referenced)}</>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Keine Urlaubshinweise vorhanden.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default VacationReminderBanner;
